import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useEvent } from 'react-use-event-hook'
import { useLiveSpaceState } from '@components/Live/LiveClientProvider'
import { TownsReputationBadge, useReputationData } from '@components/ReputationBadges'
import { InteractiveSpaceIcon } from '@components/SpaceIcon'
import { baseScanUrl } from '@components/Web3/utils'
import { Membership } from '@towns-protocol/sdk'
import { Box, Icon, IconButton, Paragraph, Stack } from '@ui'
import { SECOND_MS } from 'data/constants'
import { Analytics } from 'hooks/useAnalytics'
import useCopyToClipboard from 'hooks/useCopyToClipboard'
import { useCreateLink } from 'hooks/useCreateLink'
import { useDevice } from 'hooks/useDevice'
import { useEnvironment } from 'hooks/useEnvironment'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { useSizeContext } from 'ui/hooks/useSizeContext'
import { addressFromSpaceId, getInviteUrl } from 'ui/utils/utils'
import { SpaceData, useContractSpaceInfo, useMyMembership, useSpaceMembers } from 'use-towns-client'

import * as styles from './SpaceSideBar.css'

export const SpaceSideBarHeader = (props: {
    headerRef: React.RefObject<HTMLElement>
    space: SpaceData
    opaqueHeaderBar: boolean
}) => {
    const { opaqueHeaderBar, space } = props
    const { baseChain } = useEnvironment()
    const chainId = baseChain.id

    const { data: spaceInfo } = useContractSpaceInfo(space.id)
    const myMembership = useMyMembership(space.id)
    const { data: reputationData, hasData: hasReputationData } = useReputationData({
        spaceAddress: addressFromSpaceId(space.id),
        enabled: true,
    })

    const navigate = useNavigate()

    const onAddressClick = useEvent(() => {
        window.open(
            `${baseScanUrl(chainId)}/address/${spaceInfo?.address}`,
            '_blank',
            'noopener,noreferrer',
        )
    })

    const { createLink } = useCreateLink()

    const onTokenClick = useEvent(() => {
        const path = createLink({
            spaceId: space.id,
            panel: 'townInfo',
        })

        if (path) {
            navigate(path)
        }
    })

    const hasName = space && myMembership === Membership.Join && !!space.name

    const size = useSizeContext()
    const isSmall = size.lessThan(200)
    const { isReduceMotion } = useDevice()

    return (
        <>
            <Stack
                horizontal
                height="x6"
                zIndex="uiAbove"
                pointerEvents="none"
                className={styles.spaceHeader}
                justifyContent="spaceBetween"
            >
                <Box centerContent width="x6" pointerEvents="auto">
                    <Box
                        cursor="pointer"
                        padding="xs"
                        color={{ hover: 'default', default: 'gray2' }}
                        tooltip="Town Settings"
                        tooltipOptions={{ immediate: true }}
                        onClick={onTokenClick}
                    >
                        <Icon type="settings" size="square_sm" />
                    </Box>
                </Box>
                <Box centerContent width="x6" pointerEvents="auto">
                    <IconButton
                        icon="etherscan"
                        tooltip="Open Contract details"
                        onClick={onAddressClick}
                    />
                </Box>
            </Stack>

            {/* interactive header */}

            <Stack
                centerContent
                data-common="hey"
                paddingTop="lg"
                paddingBottom="sm"
                position="relative"
                width="100%"
                className={styles.spaceIconContainer}
                onClick={onTokenClick}
            >
                {space ? (
                    <InteractiveSpaceIcon
                        key={!isReduceMotion ? space.id : undefined}
                        size="sideBar"
                        spaceId={space.id}
                        address={spaceInfo?.address}
                        spaceName={space.name}
                    />
                ) : (
                    <Box background="level1" rounded="md" width="x17" aspectRatio="1/1" />
                )}
            </Stack>

            {/* sticky bar */}

            <Stack
                width="100%"
                position="sticky"
                top="sm"
                zIndex="ui"
                height="x4"
                justifyContent="center"
                ref={props.headerRef}
            >
                <Box
                    style={
                        opaqueHeaderBar
                            ? { opacity: 1, transition: `opacity 220ms` }
                            : { opacity: 0 }
                    }
                    position="absolute"
                    bottom="-sm"
                    background="level2"
                    boxShadow="medium"
                    height="x6"
                    width="100%"
                    pointerEvents="none"
                    roundedTop="sm"
                />

                <Box justifyContent="center" height="x3" cursor="pointer" onClick={onTokenClick}>
                    <Stack horizontal height="x4">
                        <Box width="x7" shrink={false} />
                        <Box grow position="relative">
                            <Box absoluteFill justifyContent="center">
                                {hasName && (
                                    <Stack horizontal centerContent gap="xs">
                                        <Paragraph
                                            strong
                                            truncate
                                            size={isSmall ? 'md' : 'lg'}
                                            textAlign="center"
                                        >
                                            {space.name}
                                        </Paragraph>
                                        {/* Reputation Badge - gated by feature flag */}
                                        {hasReputationData && (
                                            <Stack color="gray2">
                                                <TownsReputationBadge
                                                    totalStaked={
                                                        reputationData?.totalStaked ?? BigInt(0)
                                                    }
                                                />
                                            </Stack>
                                        )}
                                    </Stack>
                                )}
                            </Box>
                        </Box>
                        <Box width="x7" shrink={false} />
                    </Stack>
                </Box>
            </Stack>

            <Stack paddingX gap="md" insetX="xs" paddingY="xs">
                <OnlineUsersIndicator spaceId={space.id} />
                <ShareTownLinkButton spaceId={space.id} spaceName={space.name} />
            </Stack>
        </>
    )
}

const OnlineUsersIndicator = React.memo(function OnlineUsersIndicator(props: { spaceId: string }) {
    const { spaceId } = props
    const spaceState = useLiveSpaceState(spaceId)
    const { memberIds } = useSpaceMembers()
    const { openPanel } = usePanelActions()

    const totalMembers = memberIds.length
    const onlineCount = spaceState?.subscribers.totalCount ?? 0

    const onClick = useCallback(() => {
        openPanel('town-members')
    }, [openPanel])

    return (
        <Box horizontal centerContent gap="xs" cursor="pointer" onClick={onClick}>
            <Paragraph size="sm" color="gray2">
                {totalMembers} {totalMembers === 1 ? 'member' : 'members'}
            </Paragraph>
            {onlineCount > 0 && (
                <>
                    <Box
                        width="x1"
                        height="x1"
                        borderRadius="full"
                        background="presence"
                        shrink={false}
                    />
                    <Paragraph size="sm" color="gray2">
                        {onlineCount} online
                    </Paragraph>
                </>
            )}
        </Box>
    )
})

OnlineUsersIndicator.displayName = 'OnlineUsersIndicator'

const ShareTownLinkButton = React.memo(function ShareTownLinkButton(props: {
    spaceId: string
    spaceName: string
}) {
    const { spaceId, spaceName } = props
    const [, copy] = useCopyToClipboard()
    const inviteUrl = getInviteUrl({ spaceId })
    const [copyDisplay, setCopyDisplay] = useState(false)
    const text = !copyDisplay ? `Share Town Link` : 'Town link copied'

    const onCopyClick = useCallback(() => {
        copy(inviteUrl)
        setCopyDisplay(true)
        Analytics.getInstance().track('clicked on share town link', {
            spaceId,
            spaceName,
        })
    }, [copy, inviteUrl, spaceName, spaceId])

    useEffect(() => {
        if (copyDisplay) {
            const timeout = setTimeout(() => {
                setCopyDisplay(false)
            }, 3 * SECOND_MS)
            return () => {
                clearTimeout(timeout)
            }
        }
    }, [copyDisplay])

    return (
        <Box
            horizontal
            hoverable
            centerContent
            gap="sm"
            height="x5"
            borderRadius="lg"
            background="level2"
            transition="default"
            cursor="pointer"
            data-testid="share-town-link-button"
            onClick={onCopyClick}
        >
            {!copyDisplay ? (
                <Icon type="link" size="square_md" padding="xxs" />
            ) : (
                <Icon type="check" size="square_md" padding="xxs" />
            )}
            <Paragraph size="sm">{text}</Paragraph>
        </Box>
    )
})

ShareTownLinkButton.displayName = 'ShareTownLinkButton'
